// The customer page's totals: per relationship (bought, leased, rented), one tile per item with
// its count and how much of it has left. The counts are equipment_summary's (opening balances plus
// order lines). Opening balances and orders moved by hand before shipment lines existed count as
// shipped or out (api/docs/features/shipments.md, customer orders view).
import React from 'react';
import { CATEGORY } from '../components/ui/categoryColors';
import type { CategoryName } from '../components/ui/categoryColors';
import Panel, { Stat } from '../components/ui/Panel';
import Progress from '../components/ui/Progress';
import { formatEventDate } from '../utils/date-utils';
import type { CustomerOrder, CustomerTotal } from './types';

const nf = new Intl.NumberFormat('en-US');

const Tile: React.FC<{
  label: string;
  value: number;
  unit?: string;
  /** Omit for a tile with no bar. */
  moved?: number;
  /** A category's own colour, where the heading above names the category. */
  color?: string;
  done?: boolean;
  sub?: React.ReactNode;
}> = ({ label, value, unit, moved, color, done, sub }) => (
  <Panel label={label} padding="sm" className="min-w-0">
    <Stat label={label} value={value} format={nf.format} sub={unit} />
    {moved !== undefined && (
      <Progress
        className="mt-3"
        size="md"
        label={`${label} ${unit ?? ''}`.trim()}
        value={moved}
        max={value > 0 ? value : Math.max(moved, 1)}
        color={color}
        success={done}
      />
    )}
    {sub && <div className="mt-2 text-xs text-on-surface-variant">{sub}</div>}
  </Panel>
);

/**
 * One relationship (bought, leased, rented) as a row of item tiles, the way the old Equipment card
 * read: "Drones 110" with how much of it has left, per item. Items never add up across kinds.
 */
const Relationship: React.FC<{
  heading: string;
  tone: CategoryName;
  color?: string;
  totals: CustomerTotal[];
  have: (t: CustomerTotal) => number;
  moved: (t: CustomerTotal) => number;
  word: 'shipped' | 'out';
}> = ({ heading, tone, color, totals, have, moved, word }) => {
  const items = totals.filter((t) => have(t) > 0 || moved(t) > 0);
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 text-xs font-semibold" style={{ color: CATEGORY[tone].ink.oklch }}>{heading}</div>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(10rem,1fr))]">
        {items.map((t) => {
          const n = have(t);
          const m = moved(t);
          const left = n - m;
          return (
            <Tile
              key={t.key}
              label={t.label}
              value={n}
              unit={`${nf.format(m)} ${word}`}
              moved={m}
              color={color}
              done={word === 'shipped' && left <= 0}
              sub={word === 'shipped' && left > 0 ? `${nf.format(left)} still to ship` : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

const CustomerTotals: React.FC<{ totals: CustomerTotal[]; orders: CustomerOrder[] }> = ({ totals, orders }) => {
  // The newest outbound date across orders; its order names the line's detail.
  let last: CustomerOrder | null = null;
  for (const o of orders) {
    if (o.last_shipped_on && (!last || o.last_shipped_on > (last.last_shipped_on as string))) last = o;
  }
  const lastShipment = last?.shipments.find((s) => s.shipped_on === last?.last_shipped_on);

  if (!totals.some((t) => t.purchased > 0 || t.leased > 0 || t.rented > 0) && !last) return null;

  return (
    <div className="space-y-4">
      <Relationship heading="Bought" tone="green" totals={totals}
        have={(t) => t.purchased} moved={(t) => t.purchased_shipped} word="shipped" />
      <Relationship heading="On lease" tone="blue" color={CATEGORY.blue.ink.hex} totals={totals}
        have={(t) => t.leased} moved={(t) => t.leased_out} word="out" />
      <Relationship heading="On rental" tone="orange" color={CATEGORY.orange.ink.hex} totals={totals}
        have={(t) => t.rented} moved={(t) => t.rented_out} word="out" />
      {last && (
        <p className="text-xs text-on-surface-variant">
          Last shipment {formatEventDate(last.last_shipped_on as string, 'MMM D, YYYY')} on {last.so_number}
          {lastShipment?.tracking_number && (
            <>
              , tracking <span className="font-mono">{lastShipment.tracking_number}</span>
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default CustomerTotals;
